import React, { useCallback, useMemo } from "react";
import AgGridTable from "components/AgGrid/AgGridContainer";
import { AgGridTables } from "constants/aggrid";
import { Column } from "components/Layout";
import { useDispatch, useSelector } from "react-redux";
import {
  LivestockSaleAgGridRowType,
  selectSalesAggridData,
} from "selectors/aggrid/sales";
import { faPlus } from "@fortawesome/pro-solid-svg-icons";
import { setCurrentLivestockSaleId } from "actions";
import { openCommentsModal, openSaleModal } from "lib/navigation";
import { CommentTypes } from "constants/comments";
import { getIsLivestockAgent } from "selectors";
import {
  selectCanEditSaleRoles,
  selectCanViewSaleRoles,
} from "selectors/saleRoles";
import {
  agentColumnDefs,
  columnDefs,
  saleRoleColumnDefs,
} from "containers/UserDashboard/Grid/columnDefs";

interface GetRowIdProps {
  data: LivestockSaleAgGridRowType;
}

const getRowId = (params: GetRowIdProps): string =>
  String(params.data.livestockSale.id);

export function GridUserDashboard(): React.JSX.Element {
  const isLivestockAgent = useSelector(getIsLivestockAgent);
  const canViewSaleRoles = useSelector(selectCanViewSaleRoles);
  const canEditSaleRoles = useSelector(selectCanEditSaleRoles);

  const rowData = useSelector(selectSalesAggridData);

  const dispatch = useDispatch();

  const openSale = useCallback(
    (saleId: number) => {
      dispatch(setCurrentLivestockSaleId(saleId, true, false));
    },
    [dispatch],
  );

  const handleAddSale = useCallback(() => {
    openSaleModal();
  }, []);

  const openComments = (data: LivestockSaleAgGridRowType) => {
    const { deploymentSaleId } = data.livestockSale;
    if (deploymentSaleId) {
      openCommentsModal(
        CommentTypes.DEPLOYMENT_SALE,
        deploymentSaleId,
        window.location.hash,
      );
    }
  };

  const context = {
    handleOpenSale: openSale,
    handleClickComments: openComments,
  };

  const gridColumnDefs = useMemo(() => {
    if (!isLivestockAgent) {
      return columnDefs;
    }
    if (!canViewSaleRoles) {
      return agentColumnDefs;
    }
    return [...agentColumnDefs, ...saleRoleColumnDefs(canEditSaleRoles)];
  }, [isLivestockAgent, canViewSaleRoles, canEditSaleRoles]);

  const additionalActions = useMemo(
    () => [
      {
        title: "Add Sale",
        onClick: handleAddSale,
        icon: faPlus,
      },
    ],
    [handleAddSale],
  );

  return (
    <Column fullHeight>
      <AgGridTable
        additionalActions={additionalActions}
        columnDefs={gridColumnDefs}
        context={context}
        getRowId={getRowId}
        rowData={rowData}
        tableName={AgGridTables.LIVESTOCK_SALE}
        hasExportCsvPermissions
        exportSaleTitle="Sales"
      />
    </Column>
  );
}
